// Camera previews for in-studio multicam.
//
// CamPreview plays a mixer path with sub-second latency over WHEP (WebRTC
// playback, native in MediaMTX) and falls back to LL-HLS where WebRTC can't
// connect. Both paths retry forever — cameras on flaky networks (train, LTE)
// reappear as soon as they're publishing again.
//
// CamStrip shows REMOTE cameras only (CAM 2+): the local camera is already
// live in the main preview, so a delayed copy of it is never shown.
import React, { useEffect, useRef } from "react";
import { View, Pressable } from "react-native";
import tw from "twrnc";
import Hls from "hls.js";
import QRCode from "react-qr-code";
import { Ionicons } from "@expo/vector-icons";
import Text from "@/components/shared/AppText";
import useClipboardCallback from "@/components/hooks/useClipboardCallback";
import AppConfig from "@/AppConfig";
import { t } from "@/localization";

const RETRY_MS = 1500;

export const CamPreview = ({ path, cover = true }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let pc = null;
    let hls = null;
    let dead = false;
    let retryTimer = null;

    const cleanup = () => {
      try {
        pc?.close();
      } catch {}
      pc = null;
      hls?.destroy();
      hls = null;
    };
    const retry = (fn, ms = RETRY_MS) => {
      if (dead) return;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(fn, ms);
    };

    const startHls = () => {
      if (dead) return;
      const src = `${AppConfig.MIXER_HLS_BASE}/${path}/index.m3u8`;
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = src;
        video.onerror = () => retry(startHls, 2500);
        video.play().catch(() => {});
      } else if (Hls.isSupported()) {
        hls = new Hls({ liveDurationInfinity: true, lowLatencyMode: true });
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.fatal) {
            cleanup();
            retry(startWhep, 2500); // go back to preferring WHEP
          }
        });
        hls.loadSource(src);
        hls.attachMedia(video);
      }
    };

    const startWhep = async () => {
      if (dead) return;
      cleanup();
      try {
        pc = new RTCPeerConnection();
        pc.addTransceiver("video", { direction: "recvonly" });
        pc.addTransceiver("audio", { direction: "recvonly" });
        pc.ontrack = (ev) => {
          if (video && ev.streams[0] && video.srcObject !== ev.streams[0]) {
            video.srcObject = ev.streams[0];
            video.play().catch(() => {});
          }
        };
        pc.onconnectionstatechange = () => {
          if (
            pc &&
            ["failed", "disconnected", "closed"].includes(pc.connectionState)
          ) {
            cleanup();
            if (video) video.srcObject = null;
            retry(startWhep);
          }
        };
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const res = await fetch(`${AppConfig.MIXER_WHEP_BASE}/${path}/whep`, {
          method: "POST",
          headers: { "Content-Type": "application/sdp" },
          body: offer.sdp,
        });
        if (!res.ok) throw new Error(`whep ${res.status}`);
        await pc.setRemoteDescription({
          type: "answer",
          sdp: await res.text(),
        });
      } catch {
        cleanup();
        if (video) video.srcObject = null;
        // WebRTC blocked (strict NAT/firewall/UDP loss) — try HLS, and keep
        // cycling: startHls returns to startWhep on fatal errors.
        startHls();
      }
    };

    startWhep();
    return () => {
      dead = true;
      clearTimeout(retryTimer);
      cleanup();
      if (video) {
        video.srcObject = null;
        video.src = "";
        video.onerror = null;
      }
    };
  }, [path]);

  return (
    <video
      ref={videoRef}
      muted
      playsInline
      autoPlay
      style={{
        width: "100%",
        height: "100%",
        aspectRatio: "16/9",
        objectFit: cover ? "cover" : "contain",
        display: "block",
      }}
    />
  );
};

const CamTile = ({ n, baseKey, ready, onAir, mixing, onCut, onRemove }) => {
  const clipboard = useClipboardCallback();
  // Current origin so staging/preview/production each hand out their own links
  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://broadcaster.eventlive.pro";
  const link = `${origin}/stream/${baseKey}-CAM${n}`;

  return (
    <Pressable
      onPress={mixing && ready ? onCut : undefined}
      style={[
        tw`flex-1 rounded-lg overflow-hidden`,
        {
          minWidth: 150,
          maxWidth: 260,
          backgroundColor: ready ? "#000" : "#f3f4f6",
          borderWidth: 3,
          borderColor: onAir
            ? "#F84123"
            : ready && mixing
            ? "#d1d5db"
            : "transparent",
          cursor: mixing && ready ? "pointer" : "default",
        },
      ]}
    >
      {ready ? (
        <View style={{ aspectRatio: 16 / 9 }}>
          <CamPreview path={`cam/${baseKey}-CAM${n}`} />
        </View>
      ) : (
        <View
          style={[
            tw`w-full items-center justify-center py-2`,
            { aspectRatio: 16 / 9 },
          ]}
        >
          <View style={tw`bg-white p-1 rounded`}>
            <QRCode value={link} size={64} />
          </View>
          <Pressable
            onPress={() => clipboard(link)}
            style={tw`flex-row items-center mt-1.5`}
          >
            <Ionicons name="copy-outline" size={13} color="#F84123" />
            <Text style={tw`ml-1 text-xs text-[#F84123] font-semibold`}>
              {t("Copy invite link")}
            </Text>
          </Pressable>
        </View>
      )}

      <View
        style={[
          tw`absolute top-1 left-1 px-1.5 py-0.5 rounded`,
          { backgroundColor: "rgba(0,0,0,0.6)" },
        ]}
      >
        <Text style={tw`text-white text-xs font-semibold`}>
          {onAir
            ? t("CAM {number} · ON AIR", { number: n })
            : t("CAM {number}", { number: n })}
        </Text>
      </View>

      {!ready && !mixing && onRemove && (
        <Pressable
          onPress={onRemove}
          style={[
            tw`absolute top-1 right-1 w-5 h-5 rounded-full items-center justify-center`,
            { backgroundColor: "rgba(0,0,0,0.4)" },
          ]}
        >
          <Ionicons name="close" size={13} color="#fff" />
        </Pressable>
      )}
    </Pressable>
  );
};

// Remote cameras only — CAM 1 (you) is the main preview above.
const CamStrip = ({
  baseKey,
  slotCount,
  status,
  mixing,
  activeInput,
  onCut,
  onRemoveSlot,
}) => (
  <View>
    <View style={[tw`flex-row mt-3 flex-wrap`, { gap: 8 }]}>
      {Array.from({ length: Math.max(0, slotCount - 1) }, (_, i) => i + 2).map(
        (n) => (
          <CamTile
            key={n}
            n={n}
            baseKey={baseKey}
            ready={Boolean(status?.slots?.[n])}
            onAir={Boolean(mixing && activeInput === n)}
            mixing={mixing}
            onCut={() => onCut(n)}
            onRemove={n === slotCount ? () => onRemoveSlot(n) : undefined}
          />
        )
      )}
    </View>
    <Text style={tw`text-xs text-gray-400 mt-1`}>
      {mixing
        ? t(
            "Tap a camera to put it on air — tap your own preview to switch back to yourself. Viewers hear Camera 1 (you)."
          )
        : t(
            "Scan a QR code or send the invite link to add that camera. Viewers will hear Camera 1 (you)."
          )}
    </Text>
  </View>
);

export default CamStrip;
